import { inject, Inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { fiHtmlToText, fiTextToHtml, isDefined } from '@fi-shared/utils';
import { ILightWeightUser } from '@fi-shared/models';
import { FiImageSize } from '@fi-shared/types';
import { getProfileIconColor, getUserProfileAbbr } from '@fi-shared/profile-icon/profile-icon-colors';
import { FiMediaStorageService } from '@fi-shared/media-bank/fi-media-storage.service';
import { IUserProfile } from '@fi-shared/profile-icon/profile-icon.component';
import { REGEX_MATCH_TAG } from '@fi-shared/regexes';
import { isNullOrUndefined } from '@fi-shared/utils/is-null-or-undefined';

interface IUserTagData {
    userId: number;
    userFullName: string;
    userProfileImageUrl: string;
    userProfileColor: string;
    userProfileAbbr: string;
    isActivated: boolean;
}

@Injectable()
export class TextEditorTagUsersService {
    private renderer: Renderer2;
    private readonly fiMediaStorageService = inject(FiMediaStorageService);
    private readonly rendererFactory = inject(RendererFactory2);

    constructor() {
        this.renderer = this.rendererFactory.createRenderer(null, null);
    }

    //#region processing content at rendering start

    getContentWithFixedUserProfileTags(content: string, taggedUsers: ILightWeightUser[]): string {
        if (!content) {
            return;
        }
        const postHtmlItem = document.createElement('div');
        postHtmlItem.innerHTML = content;

        this.removeRedundantContentFromUserTagElements(postHtmlItem);
        this.addContentToUserTagsElements(postHtmlItem, taggedUsers);
        return postHtmlItem.innerHTML;
    }

    private removeRedundantContentFromUserTagElements(postHtmlItem: HTMLDivElement) {
        // result sample: <a class="UserTagInPost" href="/Module/Core/User/43">Amir Muawiya</a>
        // such format should be stored in database
        postHtmlItem.querySelectorAll('a.UserTagInPost').forEach((userTagElement: HTMLAnchorElement) => {
            userTagElement.classList.remove('user-tag-with-profile-image');
            const userName = this.getUserNameFromTagElement(userTagElement);
            userTagElement.innerHTML = userName;
        });
    }

    // result sample: <a class="UserTagInPost user-tag-with-profile-image" href="/Module/Core/User/43">Amir Muawiya</a>
    // <div class="fi-profile-tag-image"
    //      style="background-image: url(https://if1ee.upcloudobjects.com/platform-bucket-1/devstaging/Thumbnail/80f5c067-e738-4ef1-a15d-79b3f619b77a.jpg);"
    //      user-profile-image-url="https://if1ee.upcloudobjects.com/platform-bucket-1/devstaging/Thumbnail/80f5c067-e738-4ef1-a15d-79b3f619b77a.jpg">
    //</div><span class="user-tag-name">Amir Muawiya</span></a>
    // such markup we show in post view
    private addContentToUserTagsElements(postHtmlItem: HTMLDivElement, taggedUsers: ILightWeightUser[]) {
        postHtmlItem.querySelectorAll('a.UserTagInPost').forEach((userTagElement: HTMLAnchorElement) => {
            this.addContentToUserTagElement(userTagElement, taggedUsers);
        });
    }
    //#endregion

    //#region prepare content for editing, insert tag

    getConvertedFromViewToEditModeUserTags(content: string, taggedUsers: ILightWeightUser[]): string {
        if (!content?.length) {
            return content;
        }
        const postHtmlItem = document.createElement('div');
        postHtmlItem.innerHTML = content;

        postHtmlItem.querySelectorAll('a.UserTagInPost').forEach((userTagElement: HTMLAnchorElement) => {
            const userTagData = this.getUserTagData(userTagElement, taggedUsers);
            const ccmUserTagHtml = this.getUserTagHtml(userTagData);
            userTagElement.replaceWith(this.createElementFromHTML(ccmUserTagHtml));
        });

        return postHtmlItem.innerHTML;
    }

    getUserTagHtml(userTagData: IUserTagData): string {
        const safeFullName: string = fiTextToHtml(userTagData.userFullName);
        const userAvatarHtml = userTagData.userProfileImageUrl
            ? `<img class="user-avatar" src="${userTagData.userProfileImageUrl}">`
            : `<div class="fi-profile-colored"
                     style="background-color: ${userTagData.userProfileColor}">
                    ${userTagData.userProfileAbbr} 
               </div>`;

        let userTagClass = 'ccm-user-tag';
        if (!userTagData.isActivated) {
            userTagClass += ' disabled';
        }

        return `<div class="${userTagClass}" contenteditable="false" user-name="${userTagData.userFullName}" user-id="${userTagData.userId}" user-profile-image-url="${userTagData.userProfileImageUrl}" user-profile-color="${userTagData.userProfileColor}" user-profile-abbr="${userTagData.userProfileAbbr}">
                <div class="user-tag-container">
                    ${userAvatarHtml}
                    <span class="user-tag-name">${safeFullName} </span>
                    <span class="fas fa-times-circle CcmUserTagRemove"></span>
                </div>
            </div>`;
    }

    insertTag(element: HTMLElement, user: IUserProfile, onUserTagChanged?: () => void) {
        // skip already added tags
        const existingTags = element.querySelectorAll('.ccm-user-tag');
        for (let i = 0; i < existingTags.length; i++) {
            if (existingTags[i].getAttribute('user-id') === user.Id.toString()) {
                return false;
            }
        }

        const userTagData = this.getUserTagDataById(user.Id, user.FullName, [user as ILightWeightUser]);
        const tagToAdd = this.getUserTagHtml(userTagData);

        element.innerHTML = element.innerHTML.replace(REGEX_MATCH_TAG, '$1' + tagToAdd + '$4');

        if (onUserTagChanged) {
            onUserTagChanged();
        }

        this.applyUserTagBehavior(element, onUserTagChanged, user.Id);

        return true;
    }

    //#endregion

    //#region preparing content for save after editing
    getConvertedFromEditToViewModeUserTags(
        content: string,
        args?: {
            addContentToUserTagsElements?: boolean;
            taggedUsers?: ILightWeightUser[];
        },
    ): string {
        if (!content?.length) {
            return content;
        }

        // before replacing:
        // <div class="ccm-user-tag" contenteditable="false" user-name="Amir Muawiya" user-id="43"
        //   user-profile-image-url="https://if1ee.upcloudobjects.com/platform-bucket-1/devstaging/Thumbnail/80f5c067-e738-4ef1-a15d-79b3f619b77a.jpg"
        //   user-profile-color="" user-profile-abbr=""> <div class="user-tag-container">
        //     <img class="user-avatar" src="https://if1ee.upcloudobjects.com/platform-bucket-1/devstaging/Thumbnail/80f5c067-e738-4ef1-a15d-79b3f619b77a.jpg">
        //     <span class="user-tag-name">Amir Muawiya </span> <span class="fas fa-times-circle CcmUserTagRemove"></span></div></div>

        if (!args) {
            args = {};
        }

        let postHtmlItem = document.createElement('div');
        postHtmlItem.innerHTML = content;
        const taggedUserIds: number[] = [];
        postHtmlItem.querySelectorAll('.ccm-user-tag').forEach((userTagElement) => {
            const userAnchorEl = document.createElement('a');
            userAnchorEl.classList.add('UserTagInPost');

            if (userTagElement.classList.contains('disabled')) {
                userAnchorEl.classList.add('disabled');
            }

            const userId = parseInt(userTagElement.getAttribute('user-id'));
            taggedUserIds.push(userId);

            const userName = fiTextToHtml(userTagElement.getAttribute('user-name'));
            userAnchorEl.setAttribute('href', `/Module/Core/User/${userId}`);
            userAnchorEl.innerHTML = userName;
            userTagElement.replaceWith(userAnchorEl);
        });

        if (args?.addContentToUserTagsElements) {
            this.addContentToUserTagsElements(postHtmlItem, args.taggedUsers);
        }

        return postHtmlItem.innerHTML;
    }
    //#endregion

    //#region tag user behavior
    applyUserTagBehavior(element: HTMLElement, onUserTagChanged?: () => void, focusedUserId?: number) {
        const tags = element.querySelectorAll('.ccm-user-tag');
        for (let i = 0; i < tags.length; i++) {
            const currentElement = tags[i];
            this.renderer.setAttribute(currentElement, 'contenteditable', 'false');

            if (focusedUserId && currentElement.getAttribute('user-id') == focusedUserId.toString()) {
                this.placeCaretAfterNode(currentElement);
                document.execCommand('insertText', false, ' ');
            }

            const removeElement = currentElement.querySelector('.CcmUserTagRemove');
            if (removeElement) {
                this.renderer.listen(removeElement, 'click', () => {
                    this.renderer.removeChild(currentElement.parentNode, currentElement);
                    setTimeout(() => {
                        if (onUserTagChanged) {
                            onUserTagChanged();
                        }
                    });
                });
            }
        }
    }

    handleUserTagBackspace($event: KeyboardEvent, editorElement: HTMLElement, onContentChanged?: () => void): void {
        if ($event.key !== 'Backspace') {
            return;
        }

        const userTagElement = this.getUserTagBeforeCaret(editorElement);
        if (isNullOrUndefined(userTagElement)) {
            return;
        }

        $event.preventDefault();

        const userName = userTagElement.getAttribute('user-name') || '';
        const replacementText = document.createTextNode(`@${fiHtmlToText(userName)}`);
        userTagElement.replaceWith(replacementText);
        this.placeCaretAfterNode(replacementText);

        if (onContentChanged) {
            onContentChanged();
        }
    }

    placeCaretAfterNode(node) {
        if (typeof window.getSelection != 'undefined') {
            const range = document.createRange();
            range.setStartAfter(node);
            range.collapse(true);
            const selection = window.getSelection();
            selection.removeAllRanges();
            selection.addRange(range);
        }
    }

    private getUserTagBeforeCaret(editorElement: HTMLElement): Element | null {
        const selection = window.getSelection();
        if (!selection || !selection.isCollapsed || !selection.anchorNode) {
            return null;
        }

        const candidateNode = this.getNodeBeforeCaret(selection.anchorNode, selection.anchorOffset);
        return this.getUserTagFromCandidateNode(candidateNode, editorElement);
    }

    private getNodeBeforeCaret(anchorNode: Node, anchorOffset: number): ChildNode {
        if (anchorNode.nodeType === Node.TEXT_NODE) {
            const textBeforeCaret = (anchorNode.textContent || '').slice(0, anchorOffset);
            if (textBeforeCaret.trim()) {
                return null;
            }

            return anchorNode.previousSibling || anchorNode.parentElement?.previousSibling;
        }

        return anchorOffset ? anchorNode.childNodes[anchorOffset - 1] : null;
    }

    private getUserTagFromCandidateNode(candidateNode: ChildNode, editorElement: HTMLElement): Element | null {
        while (candidateNode && candidateNode.nodeType === Node.TEXT_NODE && !candidateNode.textContent.trim()) {
            candidateNode = candidateNode.previousSibling;
        }

        if (!(candidateNode instanceof Element)) {
            return null;
        }

        const userTagElement = candidateNode.matches('.ccm-user-tag')
            ? candidateNode
            : candidateNode.closest('.ccm-user-tag');

        return userTagElement && editorElement.contains(userTagElement) ? userTagElement : null;
    }

    //#endregion

    //# private helper methods
    private readonly USER_ID_FROM_URL_REGEX = /\/Module\/Core\/User\/(\d+)/g;

    private getUserTagData(userTagElement: HTMLAnchorElement, taggedUsers: ILightWeightUser[]): IUserTagData {
        const userName = this.getUserNameFromTagElement(userTagElement);

        const userHref = userTagElement.getAttribute('href');
        const userIdMatches = [...userHref.matchAll(this.USER_ID_FROM_URL_REGEX)];
        const userIdMatchGroups = userIdMatches.map((match) => match[1]);
        const userId = parseInt(userIdMatchGroups[0]);
        return this.getUserTagDataById(userId, userName, taggedUsers);
    }

    private getUserTagDataById(userId: number, userName: string, taggedUsers: ILightWeightUser[]): IUserTagData {
        const taggedUser = taggedUsers?.find((u) => u.Id === userId);

        let userFullName: string;
        let userProfileAbbr: string;
        let userProfileColor: string;
        let userProfileImageUrl: string;
        let isActivated: boolean;

        let mediaBankPhysicalFileName: string;
        let mediaBankExtension: string;
        if (isDefined(taggedUser)) {
            userFullName = fiTextToHtml(taggedUser.FullName);
            mediaBankPhysicalFileName = taggedUser.MediaBankPhysicalFileName;
            mediaBankExtension = taggedUser.MediaBankExtension;
            isActivated = taggedUser.IsActivated;
        } else {
            userFullName = fiTextToHtml(userName);
        }

        if (mediaBankPhysicalFileName) {
            userProfileImageUrl = this.fiMediaStorageService.getMediaFileUrlByFileName(
                mediaBankPhysicalFileName + mediaBankExtension,
                FiImageSize.Thumbnail,
            );
        } else {
            userProfileColor = getProfileIconColor(fiTextToHtml(userName));
            userProfileAbbr = getUserProfileAbbr(userName);
        }

        return {
            userId,
            userFullName,
            userProfileAbbr,
            userProfileColor,
            userProfileImageUrl,
            isActivated,
        };
    }

    private addContentToUserTagElement(userTagElement: HTMLAnchorElement, taggedUsers: ILightWeightUser[]) {
        // add required content/classes (for avatar displaying) to markup like <a class="UserTagInPost" href="/Module/Core/User/43">Amir Muawiya</a>
        const userTagData = this.getUserTagData(userTagElement, taggedUsers);
        this.addContentToUserTagElementInternal(userTagElement, userTagData);
    }
    //#endregion

    //#region insert/edit tag behavior

    //#endregion

    //#region helper methods
    private addContentToUserTagElementInternal(userTagElement: HTMLAnchorElement, userTagData: IUserTagData) {
        userTagElement.classList.add('user-tag-with-profile-image');
        if (!userTagData.isActivated) {
            userTagElement.classList.add('disabled');
        }
        let userAvatarHtml: string = '';
        if (userTagData.userProfileImageUrl) {
            userAvatarHtml = `<div class="fi-profile-tag-image" style="background-image: url(${userTagData.userProfileImageUrl});" user-profile-image-url="${userTagData.userProfileImageUrl}"></div><span class="user-tag-name">${userTagData.userFullName}</span>`;
        } else {
            userAvatarHtml = `<div class="fi-profile-colored" style="background-color: ${userTagData.userProfileColor}" user-profile-color="${userTagData.userProfileColor}">${userTagData.userProfileAbbr}</div><span class="user-tag-name">${userTagData.userFullName}</span>`;
        }
        userTagElement.innerHTML = userAvatarHtml;
    }

    private getUserNameFromTagElement(userTagElement: HTMLAnchorElement) {
        const userNameElement = userTagElement.querySelector('.user-tag-name');
        const userName = userNameElement ? userNameElement.textContent : userTagElement.textContent;
        return userName;
    }

    private createElementFromHTML(htmlString: string): HTMLElement {
        const div = document.createElement('div');
        div.innerHTML = htmlString.trim();

        // Change this to div.childNodes to support multiple top-level nodes.
        return div.firstChild as HTMLElement;
    }
    //#endregion
}
